import type { LockCardFragment$data } from "./MyLocks/__generated__/LockCardFragment.graphql";

type SpaceConfiguration = LockCardFragment$data["reservation"]["space"]["configuration"];

/**
 * Produces a readable label for a configuration, e.g.
 * `BACE_SATELLITE_MEDIUM_STANDARD` -> "Medium Standard".
 */
export const formatConfigurationLabel = (configuration: SpaceConfiguration): string => {
	const cleaned = configuration
		.replace(/^BACE_SATELLITE_/, "")
		.replace(/^BACE_STOREFRONT_/, "")
		.replace(/^BACE_/, "")
		.replace(/^THALIA_RENZ_/, "")
		.replace(/_/g, " ")
		.toLowerCase();

	return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
};

/** Millimetres -> centimetres, trimming trailing `.0` (e.g. 215 -> "21,5"). */
export const mmToCm = (mm: number): string => {
	const cm = mm / 10;
	return Number.isInteger(cm) ? String(cm) : cm.toFixed(1).replace(".", ",");
};

export const formatDimensions = (dimensions: { widthMm: number; heightMm: number; depthMm: number }): string =>
	`${mmToCm(dimensions.widthMm)} x ${mmToCm(dimensions.heightMm)} x ${mmToCm(dimensions.depthMm)} cm`;

/** Size buckets used to filter compartments in the rent screen. */
export type SizeBucket = "S" | "M" | "L" | "XL" | "2XL";

/**
 * Derives a size bucket (S/M/L/XL) from a configuration name, e.g.
 * `BACE_SATELLITE_MEDIUM_STANDARD` -> "M". Returns null when no known size
 * token is present so the caller can decide how to bucket it.
 */
export const getSizeBucket = (configuration: SpaceConfiguration): SizeBucket | null => {
	const tokens = configuration.toUpperCase().split("_");
	if (tokens.includes("2XL")) return "2XL";
	if (tokens.includes("XL")) return "XL";
	if (tokens.includes("LARGE") || tokens.includes("L")) return "L";
	if (tokens.includes("MEDIUM") || tokens.includes("M")) return "M";
	if (tokens.includes("SMALL") || tokens.includes("S")) return "S";
	return null;
};

/** Fixed display order for size filter tabs. */
export const SIZE_BUCKET_ORDER: SizeBucket[] = ["S", "M", "L", "XL", "2XL"];
